import CancelIcon from "@/assets/xmark.png";
import { CalculatorState } from "@/types/calculatorTypes";
import { EmployeeTokenData } from "@/types/contributions";
import { getDecodedTokenSafe } from "@/utils/tokenUtils";
import React, { useEffect, useState } from "react";
import DetailedCalculator from "./DetailedCalculator";
import SimplifiedCalculator from "./SimplifiedCalculator";
import TabNavigation from "./components/TabNavigation";
import { CalculatorTabName, DEFAULT_CALCULATOR_VALUES } from "./constants";

interface ContributionsCalculatorProps {
  onCancel?: () => void;
}

const ContributionsCalculator: React.FC<ContributionsCalculatorProps> = ({
  onCancel,
}) => {
  const [activeTab, setActiveTab] = useState<CalculatorTabName>(
    CalculatorTabName.Simplified,
  );

  const [isLoading, setIsLoading] = useState(true);

  const [calculatorState, setCalculatorState] = useState<CalculatorState>({
    annualSalary: 0,
    employeeContributionRate: 0,
    employerMatchRate: 0,
    maxEmployerMatchSalaryRate: 0,
    annualGrowthRate: DEFAULT_CALCULATOR_VALUES.ANNUAL_GROWTH_RATE,
    maxAnnualContribution: 0,
  });

  const fetchEmployerContribution = async () => {
    const decodedToken = getDecodedTokenSafe<EmployeeTokenData>(() => {
      // Fallback
      setCalculatorState((prev) => ({
        ...prev,
        annualSalary: DEFAULT_CALCULATOR_VALUES.ANNUAL_SALARY,
        employerMatchRate: DEFAULT_CALCULATOR_VALUES.EMPLOYER_MATCH_RATE,
        maxEmployerMatchSalaryRate:
          DEFAULT_CALCULATOR_VALUES.MAX_EMPLOYER_MATCH_SALARY_RATE,
        maxAnnualContribution:
          DEFAULT_CALCULATOR_VALUES.MAX_ANNUAL_CONTRIBUTION,
      }));
    });

    if (decodedToken) {
      setCalculatorState((prev) => ({
        ...prev,
        annualSalary: decodedToken.EmployeeSalary,
        employerMatchRate: DEFAULT_CALCULATOR_VALUES.EMPLOYER_MATCH_RATE,
        maxEmployerMatchSalaryRate: decodedToken.EmployerMaxContribution,
        maxAnnualContribution:
          decodedToken.EmployeeSalary *
          (decodedToken.EmployerMaxContribution / 100),
      }));
    }

    setIsLoading(false);
  };

  useEffect(() => {
    fetchEmployerContribution();
  }, []);

  const handleTabClick = (tabName: CalculatorTabName) => {
    setActiveTab(tabName);
  };

  const updateSalary = (salary: number) => {
    setCalculatorState((prev) => ({ ...prev, annualSalary: salary }));
  };

  const updateContributionRate = (rate: number) => {
    setCalculatorState((prev) => ({ ...prev, employeeContributionRate: rate }));
  };

  const updateEmployerMatchRate = (rate: number) => {
    setCalculatorState((prev) => ({ ...prev, employerMatchRate: rate }));
  };

  const updateMaxEmployerMatchSalaryRate = (rate: number) => {
    setCalculatorState((prev) => ({
      ...prev,
      maxEmployerMatchSalaryRate: rate,
    }));
  };

  const updateAnnualGrowthRate = (rate: number) => {
    setCalculatorState((prev) => ({ ...prev, annualGrowthRate: rate }));
  };

  const updateMaxAnnualContribution = (amount: number) => {
    setCalculatorState((prev) => ({ ...prev, maxAnnualContribution: amount }));
  };

  const calculatorActions = {
    updateSalary,
    updateContributionRate,
    updateEmployerMatchRate,
    updateMaxEmployerMatchSalaryRate,
    updateAnnualGrowthRate,
    updateMaxAnnualContribution,
  };

  if (isLoading) {
    return (
      <div className="px-[24px] pt-[24px] pb-[8px]">
        <div className="flex justify-between items-center">
          <h3 className="leading-[127%] font-semibold text-[24px]">
            Contribution calculator
          </h3>
          <img
            src={CancelIcon}
            onClick={onCancel}
            className="cursor-pointer w-[15px] h-[15px]"
            alt="Close"
          />
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00ADAC] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your information...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="px-[24px] pt-[24px] pb-[8px]">
        <div className="flex justify-between items-center">
          <h3 className="leading-[127%] font-semibold text-[24px]">
            Contribution calculator
          </h3>
          <img
            src={CancelIcon}
            onClick={onCancel}
            className="cursor-pointer w-[15px] h-[15px]"
            alt="Close"
          />
        </div>

        <TabNavigation activeTab={activeTab} onTabClick={handleTabClick} />

        <div>
          {activeTab === CalculatorTabName.Simplified && (
            <SimplifiedCalculator
              calculatorState={calculatorState}
              onSalaryChange={calculatorActions.updateSalary}
              onContributionRateChange={
                calculatorActions.updateContributionRate
              }
              handleEditDetails={() =>
                handleTabClick(CalculatorTabName.Detailed)
              }
            />
          )}
          {activeTab === CalculatorTabName.Detailed && (
            <DetailedCalculator
              calculatorState={calculatorState}
              onSalaryChange={calculatorActions.updateSalary}
              onContributionRateChange={
                calculatorActions.updateContributionRate
              }
              onEmployerMatchRateChange={
                calculatorActions.updateEmployerMatchRate
              }
              onMaxEmployerMatchSalaryRateChange={
                calculatorActions.updateMaxEmployerMatchSalaryRate
              }
              onAnnualGrowthRateChange={
                calculatorActions.updateAnnualGrowthRate
              }
              onMaxAnnualContributionChange={
                calculatorActions.updateMaxAnnualContribution
              }
            />
          )}
        </div>
      </div>
    </>
  );
};

export default ContributionsCalculator;
